import type { IconType } from "react-icons";
import {
  IoAlbumsOutline,
  IoBookOutline,
  IoBriefcaseOutline,
  IoBuildOutline,
  IoHomeOutline,
  IoCalendarOutline,
  IoDocumentTextOutline,
  IoGridOutline,
  IoImagesOutline,
  IoInformationCircleOutline,
  IoLayersOutline,
  IoLockClosedOutline,
  IoMailOutline,
  IoNewspaperOutline,
  IoPeopleOutline,
  IoSchoolOutline,
  IoSearchOutline,
  IoSettingsOutline,
  IoStorefrontOutline,
  IoShieldCheckmarkOutline,
  IoSwapHorizontalOutline,
  IoTextOutline,
  IoTimeOutline,
} from "react-icons/io5";
import type { PermissionAction, PermissionResource } from "@/api/types";

export interface NavLeaf {
  label: string;
  to: string;
  icon: IconType;
  resource: PermissionResource;
  action: PermissionAction;
}

export interface NavGroup {
  label: string;
  icon: IconType;
  /** Revealed when the group is opened; each is permission-checked in its own right. */
  children: NavLeaf[];
}

export type NavEntry = NavLeaf | NavGroup;

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return "children" in entry;
}

/**
 * The admin menu.
 *
 * The top half mirrors the public site's own navigation — Artists,
 * Exhibitions, Practices, Publications, About — so an editor looking for the
 * page they just visited finds it in the same place and the same order.
 *
 * Below it, the day-to-day tools stand on their own and only the occasional
 * ones are folded away: Media Library, the slider, SEO, Subscribers and
 * Settings are reached in one click, while page furniture and account
 * management sit in a group each.
 */
export const NAV_ITEMS: NavEntry[] = [
  { label: "Dashboard", to: "/", icon: IoGridOutline, resource: "DASHBOARD", action: "READ" },

  { label: "Artists", to: "/artists", icon: IoPeopleOutline, resource: "ARTISTS", action: "READ" },
  { label: "Exhibitions", to: "/exhibitions", icon: IoCalendarOutline, resource: "EXHIBITIONS", action: "READ" },
  {
    label: "Practices",
    icon: IoLayersOutline,
    children: [
      { label: "Art Residency", to: "/practices/art-residency", icon: IoHomeOutline, resource: "PRACTICES", action: "READ" },
      { label: "Art Fairs", to: "/practices/art-fairs", icon: IoStorefrontOutline, resource: "PRACTICES", action: "READ" },
      { label: "Partnerships", to: "/practices/partnerships", icon: IoBriefcaseOutline, resource: "PRACTICES", action: "READ" },
      { label: "Art Education", to: "/practices/art-education", icon: IoSchoolOutline, resource: "PRACTICES", action: "READ" },
    ],
  },
  {
    label: "Publications",
    icon: IoBookOutline,
    // Named as the site names them: the visitor's "Artist Books" is this
    // admin's Publications, and "Press & News" is News.
    children: [
      { label: "Artist Books", to: "/publications", icon: IoBookOutline, resource: "PUBLICATIONS", action: "READ" },
      { label: "Press & News", to: "/news", icon: IoNewspaperOutline, resource: "NEWS", action: "READ" },
    ],
  },
  {
    // The site's About menu holds exactly these two.
    label: "About",
    icon: IoInformationCircleOutline,
    children: [
      { label: "Our Story", to: "/our-story", icon: IoDocumentTextOutline, resource: "PAGES", action: "READ" },
      { label: "Contact", to: "/contact", icon: IoMailOutline, resource: "CONTACT_SUBMISSIONS", action: "READ" },
    ],
  },

  { label: "Media Library", to: "/media", icon: IoImagesOutline, resource: "MEDIA", action: "READ" },
  // The home page's rotating banner. "Carousel" named the mechanism; this names
  // the thing an editor is actually changing.
  { label: "Art Gallery Slider", to: "/carousel", icon: IoAlbumsOutline, resource: "CAROUSEL", action: "READ" },
  { label: "SEO & Redirects", to: "/seo", icon: IoSearchOutline, resource: "SEO", action: "READ" },

  {
    label: "Site Setup",
    icon: IoBuildOutline,
    children: [
      { label: "Page Headings", to: "/page-headings", icon: IoTextOutline, resource: "PAGES", action: "READ" },
      { label: "Tags & Categories", to: "/taxonomy", icon: IoSwapHorizontalOutline, resource: "TAXONOMY", action: "READ" },
    ],
  },

  { label: "Subscribers", to: "/subscribers", icon: IoMailOutline, resource: "SUBSCRIBERS", action: "READ" },

  {
    label: "Administration",
    icon: IoLockClosedOutline,
    children: [
      { label: "Users", to: "/users", icon: IoPeopleOutline, resource: "USERS", action: "READ" },
      { label: "Roles", to: "/roles", icon: IoShieldCheckmarkOutline, resource: "ROLES", action: "READ" },
      { label: "Audit Logs", to: "/audit-logs", icon: IoTimeOutline, resource: "AUDIT_LOGS", action: "READ" },
    ],
  },

  { label: "Settings", to: "/settings", icon: IoSettingsOutline, resource: "SETTINGS", action: "READ" },
];
